import POSButton from '@/components/POS/Common/POSButton';
import POSHeading from '@/components/POS/Common/POSHeading';
import POSInput from '@/components/POS/Common/POSInput';
import POSSelect from '@/components/POS/Common/POSSelect';
import POSSwitch from '@/components/POS/Common/POSSwitch';
import { AppBar, Box, Divider, Grid2, Skeleton, Stack, Typography } from '@mui/material';
import { SelectChangeEvent } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useFormik } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiSearch } from 'react-icons/fi';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { api } from '@/utils/Api/POS';
// @ts-ignore
import { GetServiceGroup } from '@/utils/Api/Service';
import { TreatmentGroup, TreatmentItem } from '@/scenes/BookingDiscounts/Types/booking-discounts.types';

interface InitialValueDataType {
    enabled: boolean;
    webhookUrl: string;
    event: string;
    secret: string;
    includeServiceData: boolean;
    allServices: boolean;
    serviceIds: number[];
    webhookId?: number;
}

const defaultValues: InitialValueDataType = {
    enabled: false,
    webhookUrl: '',
    event: 'marketplace-booking',
    secret: '',
    includeServiceData: false,
    allServices: true,
    serviceIds: [],
};

const POSWebhook = () => {
    const [isSaveChanges, setSaveChanegs] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [showSecret, setShowSecret] = useState(false);

    const handleToggleSecret = () => {
        setShowSecret((prev) => !prev);
    };

    const [initialValues, setInitialValues] = useState<InitialValueDataType>(defaultValues);

    const [duplicateData, setDuplicateData] = useState<InitialValueDataType>(defaultValues);

    const [serviceGroups, setServiceGroups] = useState<TreatmentGroup[]>([]);
    const [servicesLoading, setServicesLoading] = useState(false);
    const [serviceSearch, setServiceSearch] = useState('');
    const [collapsedServiceGroups, setCollapsedServiceGroups] = useState<Record<string, boolean>>({});

    const eventOptions = [{ label: t('Integration.WebhookEventMarketplaceBooking'), value: 'marketplace-booking' }];

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validationSchema: Yup.object({
            enabled: Yup.boolean(),
            webhookUrl: Yup.string()
                .nullable()
                .when('enabled', {
                    is: true,
                    then: (schema) =>
                        schema.required(t('Integration.WebhookUrlRequired')).url(t('Integration.WebhookUrlInvalid')),
                    otherwise: (schema) => schema.notRequired(),
                }),
            event: Yup.string()
                .nullable()
                .when('enabled', {
                    is: true,
                    then: (schema) => schema.required(t('Integration.WebhookEventRequired')),
                    otherwise: (schema) => schema.notRequired(),
                }),
            includeServiceData: Yup.boolean(),
            allServices: Yup.boolean(),
            serviceIds: Yup.array()
                .of(Yup.number())
                .when(['enabled', 'includeServiceData', 'allServices'], {
                    is: (enabled: boolean, includeServiceData: boolean, allServices: boolean) =>
                        enabled && includeServiceData && !allServices,
                    then: (schema) => schema.min(1, t('Integration.WebhookServicesRequired')),
                    otherwise: (schema) => schema.notRequired(),
                }),
        }),

        onSubmit: (values) => {
            callPostApi(values);
        },
    });

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch all webhooks and filter for marketplace-booking event
            const response = await api.getApiWebhooks();
            const responseData = response as any;
            // Handle both direct response and wrapped response
            const webhooks = responseData?.webhooks || responseData?.data?.webhooks || [];

            // Find webhook for marketplace-booking event
            const webhook = webhooks.find((wh: any) => wh.event === 'marketplace-booking');

            if (webhook) {
                const webhookServiceIds: number[] | null | undefined = webhook.serviceIds;
                const values: InitialValueDataType = {
                    enabled: webhook.active || false,
                    webhookUrl: webhook.webhookUrl || '',
                    event: webhook.event || 'marketplace-booking',
                    secret: webhook.secret || '',
                    includeServiceData: webhook.includeServiceData || false,
                    allServices: !webhookServiceIds || webhookServiceIds.length === 0,
                    serviceIds: webhookServiceIds || [],
                    webhookId: webhook.id,
                };
                setInitialValues(values);
                setDuplicateData(values);
            } else {
                // No webhook found, set defaults
                setInitialValues(defaultValues);
                setDuplicateData(defaultValues);
            }
        } catch (error) {
            console.error('Error : ', error);
            // Set defaults on error
            setInitialValues(defaultValues);
            setDuplicateData(defaultValues);
        } finally {
            setLoading(false);
        }
    };

    const fetchServices = async () => {
        setServicesLoading(true);
        try {
            const response = await GetServiceGroup();
            const groupList = response?.data?.data || [];
            if (!Array.isArray(groupList)) {
                setServiceGroups([]);
                return;
            }

            const groups: TreatmentGroup[] = groupList
                .map((group: any) => {
                    const groupId = Number(group?.id ?? 0);
                    return {
                        groupId,
                        title: group?.group || '',
                        sequence: Number(group?.sequence ?? groupId),
                        services: (Array.isArray(group?.services) ? group.services : []).map(
                            (service: any): TreatmentItem => ({
                                id: Number(service?.id),
                                name: service?.name || '',
                                price: Number(service?.price || 0),
                                groupId,
                                sequence: service?.sequence ?? null,
                            }),
                        ),
                    };
                })
                .filter((group: TreatmentGroup) => group.services.length > 0);

            setServiceGroups(groups);
        } catch (error) {
            console.error('Error : ', error);
            setServiceGroups([]);
        } finally {
            setServicesLoading(false);
        }
    };

    const filteredServiceGroups = useMemo(() => {
        const query = serviceSearch.trim().toLowerCase();
        if (!query) return serviceGroups;

        return serviceGroups
            .map((group) => {
                const groupMatches = group.title.toLowerCase().includes(query);
                const filteredServices = groupMatches
                    ? group.services
                    : group.services.filter((service) => service.name.toLowerCase().includes(query));
                return { ...group, services: filteredServices };
            })
            .filter((group) => group.services.length > 0);
    }, [serviceGroups, serviceSearch]);

    const isGroupFullySelected = (groupServiceIds: number[]): boolean =>
        groupServiceIds.length > 0 && groupServiceIds.every((id) => formik.values.serviceIds.includes(id));

    const toggleServiceSelection = (serviceId: number, checked: boolean) => {
        const current = formik.values.serviceIds;
        formik.setFieldValue(
            'serviceIds',
            checked ? Array.from(new Set([...current, serviceId])) : current.filter((id) => id !== serviceId),
        );
        formik.setFieldTouched('serviceIds', true);
    };

    const toggleGroupSelection = (groupServiceIds: number[], checked: boolean) => {
        const current = formik.values.serviceIds;
        formik.setFieldValue(
            'serviceIds',
            checked
                ? Array.from(new Set([...current, ...groupServiceIds]))
                : current.filter((id) => !groupServiceIds.includes(id)),
        );
        formik.setFieldTouched('serviceIds', true);
    };

    const callPostApi = async (values: InitialValueDataType) => {
        setLoading(true);
        try {
            const payload = {
                event: values.event,
                webhookUrl: values.webhookUrl,
                secret: values.secret || undefined,
                active: values.enabled,
                includeServiceData: values.includeServiceData,
                serviceIds: values.includeServiceData && !values.allServices ? values.serviceIds : null,
            };

            let response: any;
            if (values.webhookId) {
                // Update existing webhook
                response = await api.putApiWebhooksId(values.webhookId, payload);
            } else {
                // Create new webhook
                response = await api.postApiWebhooks(payload);
            }

            // Handle response - could be direct or wrapped
            const responseData = response as any;
            if (responseData?.id || responseData?.data?.id) {
                const webhookId = responseData.id || responseData.data.id;
                values.webhookId = webhookId;
                setSaveChanegs(false);
                setDuplicateData(values);
                setInitialValues(values);
                toast.success(t('Services.ToastUpSuccess'));
            } else if (response) {
                // Response exists but no id (update case)
                setSaveChanegs(false);
                setDuplicateData(values);
                setInitialValues(values);
                toast.success(t('Services.ToastUpSuccess'));
            }
        } catch (error) {
            console.error('Error : ', error);
            toast.error(t('Services.ToastUpErr'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        fetchServices();
    }, []);

    useEffect(() => {
        const a = JSON.stringify(formik.values || {});
        const b = JSON.stringify(duplicateData || {});
        setSaveChanegs(a !== b);
    }, [formik.values, duplicateData]);

    return (
        <Box p={4.5}>
            {isSaveChanges && (
                <AppBar
                    sx={{
                        position: 'fixed',
                        top: 45,
                        left: 0,
                        right: 0,
                        py: 1,
                        px: 4,
                        bgcolor: '#fff',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        width: '100%',
                        zIndex: 8,
                        mb: 5,
                    }}
                >
                    <POSButton
                        variant="save"
                        width="auto"
                        height={35}
                        title={t('Setting.SaveChanges')}
                        onClick={formik.handleSubmit}
                        loading={isLoading}
                    />
                </AppBar>
            )}

            <React.Fragment>
                <Grid2
                    container
                    sx={{ p: { xs: 2, md: 5 }, background: '#fff', borderRadius: '25px', mt: isSaveChanges ? 7 : 0 }}
                >
                    <Grid2 size={{ xs: 12, md: 4 }}>
                        <POSHeading text={t('Integration.Webhook')} />
                        <POSHeading
                            sx={{ fontWeight: 400 }}
                            fontSize={16}
                            fontColor="#666"
                            text={t('Integration.WebhookDescription')}
                        />
                    </Grid2>
                    {isLoading ? (
                        <Stack sx={{ width: '65%' }}>
                            <Skeleton variant="rounded" sx={{ width: '100%' }} height={50} />
                        </Stack>
                    ) : (
                        <Grid2 size={{ xs: 12, md: 8 }}>
                            <Stack spacing={3}>
                                <Box>
                                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                                        {t('Integration.EnableWebhook')}
                                    </Typography>
                                    <POSSwitch
                                        checked={formik.values.enabled}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            formik.setFieldValue('enabled', isChecked);

                                            if (!isChecked) {
                                                formik.setFieldValue('webhookUrl', duplicateData?.webhookUrl);
                                                formik.setFieldValue('event', duplicateData?.event);
                                                formik.setFieldValue('secret', duplicateData?.secret);
                                                formik.setFieldValue(
                                                    'includeServiceData',
                                                    duplicateData?.includeServiceData,
                                                );
                                                formik.setFieldValue('allServices', duplicateData?.allServices);
                                                formik.setFieldValue('serviceIds', duplicateData?.serviceIds);
                                            }
                                        }}
                                    />
                                </Box>

                                <Box sx={{ display: formik?.values?.enabled ? 'block' : 'none' }}>
                                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                                        {t('Integration.WebhookUrl')}
                                    </Typography>
                                    <POSInput
                                        type="url"
                                        value={formik.values.webhookUrl}
                                        onChange={(e) => formik.setFieldValue('webhookUrl', e.target.value)}
                                        onBlur={() => formik.setFieldTouched('webhookUrl', true)}
                                        sx={{ width: '100%' }}
                                        error={formik.touched.webhookUrl && Boolean(formik.errors.webhookUrl)}
                                        placeholder={t('Integration.WebhookUrlPlaceholder')}
                                    />

                                    {formik.touched.webhookUrl && formik.errors.webhookUrl && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {formik.errors.webhookUrl}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ display: formik?.values?.enabled ? 'block' : 'none' }}>
                                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                                        {t('Integration.WebhookEvent')}
                                    </Typography>
                                    <POSSelect
                                        value={formik.values.event || ''}
                                        onChange={(
                                            e: SelectChangeEvent<string | number | (string | number)[] | null>,
                                        ) => {
                                            formik.setFieldValue('event', (e.target.value as string) || '');
                                        }}
                                        onBlur={() => formik.setFieldTouched('event', true)}
                                        options={eventOptions}
                                        placeholderText={t('Integration.WebhookEventPlaceholder')}
                                        error={formik.touched.event && Boolean(formik.errors.event)}
                                        helperText={
                                            formik.touched.event && formik.errors.event ? formik.errors.event : ''
                                        }
                                        sx={{ width: '100%' }}
                                    />
                                </Box>

                                <Box sx={{ display: formik?.values?.enabled ? 'block' : 'none' }}>
                                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                                        {t('Integration.WebhookSecret')}
                                    </Typography>
                                    <POSInput
                                        type={showSecret ? 'text' : 'password'}
                                        value={formik.values.secret}
                                        onChange={(e) => formik.setFieldValue('secret', e.target.value)}
                                        onBlur={() => formik.setFieldTouched('secret', true)}
                                        sx={{ width: '100%' }}
                                        error={formik.touched.secret && Boolean(formik.errors.secret)}
                                        placeholder={t('Integration.WebhookSecretPlaceholder')}
                                        autoComplete={'new-password'}
                                        slotProps={{
                                            input: {
                                                endAdornment: (
                                                    <Box style={{ cursor: 'pointer' }} onClick={handleToggleSecret}>
                                                        {showSecret ? <Visibility /> : <VisibilityOff />}
                                                    </Box>
                                                ),
                                            },
                                        }}
                                    />

                                    {formik.touched.secret && formik.errors.secret && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                            {formik.errors.secret}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ display: formik?.values?.enabled ? 'block' : 'none' }}>
                                    <Typography fontWeight={700} sx={{ mb: 1 }}>
                                        {t('Integration.WebhookIncludeServiceData')}
                                    </Typography>
                                    <Typography fontSize={14} color="#666" sx={{ mb: 1 }}>
                                        {t('Integration.WebhookIncludeServiceDataDescription')}
                                    </Typography>
                                    <POSSwitch
                                        checked={formik.values.includeServiceData}
                                        onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            formik.setFieldValue('includeServiceData', isChecked);

                                            if (!isChecked) {
                                                formik.setFieldValue('allServices', true);
                                                formik.setFieldValue('serviceIds', []);
                                            }
                                        }}
                                    />
                                </Box>

                                {formik.values.enabled && formik.values.includeServiceData && (
                                    <Box>
                                        <Typography fontWeight={700} sx={{ mb: 1 }}>
                                            {t('Integration.WebhookServices')}
                                        </Typography>
                                        <POSSelect
                                            value={formik.values.allServices ? 'all' : 'specific'}
                                            onChange={(
                                                e: SelectChangeEvent<string | number | (string | number)[] | null>,
                                            ) => {
                                                const isAll = e.target.value === 'all';
                                                formik.setFieldValue('allServices', isAll);
                                                if (isAll) {
                                                    formik.setFieldValue('serviceIds', []);
                                                }
                                            }}
                                            options={[
                                                { label: t('Integration.WebhookAllServices'), value: 'all' },
                                                { label: t('Integration.WebhookSpecificServices'), value: 'specific' },
                                            ]}
                                            sx={{ width: '100%', mb: formik.values.allServices ? 0 : 1.5 }}
                                        />

                                        {!formik.values.allServices && (
                                            <Stack spacing={1.5}>
                                                <POSInput
                                                    value={serviceSearch}
                                                    onChange={(e) => setServiceSearch(e.target.value)}
                                                    sx={{ width: '100%' }}
                                                    placeholder={t('Integration.WebhookServicesSearchPlaceholder')}
                                                    startIcon={<FiSearch size={16} />}
                                                />

                                                {formik.touched.serviceIds && formik.errors.serviceIds && (
                                                    <Typography variant="caption" color="error">
                                                        {formik.errors.serviceIds as string}
                                                    </Typography>
                                                )}

                                                <Box
                                                    sx={{
                                                        maxHeight: 380,
                                                        overflowY: 'auto',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        borderRadius: 1.5,
                                                        p: 1.5,
                                                    }}
                                                >
                                                    {servicesLoading ? (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            align="center"
                                                            sx={{ py: 4 }}
                                                        >
                                                            {t('Common.Loading')}
                                                        </Typography>
                                                    ) : filteredServiceGroups.length === 0 ? (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            align="center"
                                                            sx={{ py: 4 }}
                                                        >
                                                            {t('Integration.WebhookNoServicesFound')}
                                                        </Typography>
                                                    ) : (
                                                        filteredServiceGroups.map((group: TreatmentGroup) => {
                                                            const groupKey = String(group.groupId);
                                                            const groupServiceIds = group.services.map(
                                                                (service: TreatmentItem) => service.id,
                                                            );
                                                            const isCollapsed = collapsedServiceGroups[groupKey];
                                                            const groupTitle =
                                                                group.groupId === 0
                                                                    ? t('Services.ServiceWithoutGRP')
                                                                    : group.title;

                                                            return (
                                                                <Box
                                                                    key={groupKey}
                                                                    sx={{
                                                                        mb: 1.5,
                                                                        border: '1px solid',
                                                                        borderColor: 'divider',
                                                                        borderRadius: 1,
                                                                        overflow: 'hidden',
                                                                        '&:last-of-type': { mb: 0 },
                                                                    }}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        justifyContent="space-between"
                                                                        alignItems="center"
                                                                        sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}
                                                                    >
                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="center"
                                                                            spacing={1}
                                                                            sx={{ cursor: 'pointer' }}
                                                                            onClick={() =>
                                                                                setCollapsedServiceGroups((prev) => ({
                                                                                    ...prev,
                                                                                    [groupKey]: !prev[groupKey],
                                                                                }))
                                                                            }
                                                                        >
                                                                            {isCollapsed ? (
                                                                                <FiChevronRight size={16} />
                                                                            ) : (
                                                                                <FiChevronDown size={16} />
                                                                            )}
                                                                            <Typography
                                                                                variant="subtitle2"
                                                                                sx={{ fontWeight: 700 }}
                                                                            >
                                                                                {groupTitle} ({group.services.length})
                                                                            </Typography>
                                                                        </Stack>

                                                                        <Stack
                                                                            direction="row"
                                                                            alignItems="center"
                                                                            spacing={1}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {t(
                                                                                    'Integration.WebhookSelectAllInGroup',
                                                                                )}
                                                                            </Typography>
                                                                            <POSSwitch
                                                                                checked={isGroupFullySelected(
                                                                                    groupServiceIds,
                                                                                )}
                                                                                onChange={(e) =>
                                                                                    toggleGroupSelection(
                                                                                        groupServiceIds,
                                                                                        e.target.checked,
                                                                                    )
                                                                                }
                                                                            />
                                                                        </Stack>
                                                                    </Stack>

                                                                    {!isCollapsed && (
                                                                        <Stack divider={<Divider />}>
                                                                            {group.services.map(
                                                                                (service: TreatmentItem) => (
                                                                                    <Stack
                                                                                        key={service.id}
                                                                                        direction="row"
                                                                                        justifyContent="space-between"
                                                                                        alignItems="center"
                                                                                        sx={{ px: 2, py: 1.25 }}
                                                                                    >
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{ fontWeight: 500 }}
                                                                                        >
                                                                                            {service.name}
                                                                                        </Typography>
                                                                                        <POSSwitch
                                                                                            checked={formik.values.serviceIds.includes(
                                                                                                service.id,
                                                                                            )}
                                                                                            onChange={(e) =>
                                                                                                toggleServiceSelection(
                                                                                                    service.id,
                                                                                                    e.target.checked,
                                                                                                )
                                                                                            }
                                                                                        />
                                                                                    </Stack>
                                                                                ),
                                                                            )}
                                                                        </Stack>
                                                                    )}
                                                                </Box>
                                                            );
                                                        })
                                                    )}
                                                </Box>
                                            </Stack>
                                        )}
                                    </Box>
                                )}
                            </Stack>
                        </Grid2>
                    )}
                </Grid2>
            </React.Fragment>
        </Box>
    );
};

export default POSWebhook;
